import {
  GetMediaHouseFolderPathDocument,
  GetMediaHouseFoldersDocument,
  type GetMediaHouseFoldersQuery,
  type GetMediaHouseFoldersQueryVariables,
  type MediaHouseFolder,
} from "@/generated/graphql";
import { Button } from "@ai-teach/ui-web";
import { useQuery } from "@apollo/client/react";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  FiAlertTriangle,
  FiChevronDown,
  FiChevronRight,
  FiFolder,
  FiFolderPlus,
  FiHome,
  FiMoreVertical,
  FiRefreshCw,
} from "react-icons/fi";
import { CreateFolderModal } from "../modals/folders/CreateFolderModal";
import { RenameFolderModal } from "../modals/folders/RenameFolderModal";

interface FoldersSectionProps {
  parentId: string | null;
  mediaType: "image" | "video" | "audio";
  onFolderClick: (folderId: string) => void;
  disabled?: boolean;
}

// Subtle color variations for folder icons by index
const folderAccents = [
  "text-primary-500",
  "text-secondary-500",
  "text-accent-500",
  "text-violet-500",
  "text-rose-400",
  "text-sky-400",
];

export function FoldersSection({
  parentId,
  mediaType,
  onFolderClick,
  disabled = false,
}: FoldersSectionProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState<MediaHouseFolder | null>(null);
  const [contextMenuFolder, setContextMenuFolder] = useState<MediaHouseFolder | null>(null);
  const [contextMenuPosition, setContextMenuPosition] = useState({ x: 0, y: 0 });

  const observerTarget = useRef<HTMLDivElement>(null);

  const { data: pathData } = useQuery(GetMediaHouseFolderPathDocument, {
    variables: { id: parentId || "" },
    skip: !parentId,
    fetchPolicy: "cache-and-network",
  });

  const { data, loading, error, fetchMore, refetch } = useQuery<
    GetMediaHouseFoldersQuery,
    GetMediaHouseFoldersQueryVariables
  >(GetMediaHouseFoldersDocument, {
    variables: { parentId, type: mediaType, first: 6 },
    fetchPolicy: "cache-and-network",
  });

  const folders = data?.mediaHouseFolders?.edges?.map((e) => e.node) || [];
  const pageInfo = data?.mediaHouseFolders?.pageInfo;
  const totalCount = data?.mediaHouseFolders?.totalCount || 0;
  const hasMore = pageInfo?.hasNextPage || false;
  const breadcrumbs = pathData?.mediaHouseFolderPath || [];

  const isServerError = (err: unknown): boolean => {
    if (!err || typeof err !== "object") return false;
    const error = err as { networkError?: unknown; graphQLErrors?: unknown[] };
    if (error.networkError) return true;
    const serverErrorCodes = ["INTERNAL_SERVER_ERROR", "SERVICE_UNAVAILABLE"];
    return (
      error.graphQLErrors?.some((e: unknown) => {
        const graphQLError = e as { extensions?: { code?: string } };
        return serverErrorCodes.includes(graphQLError.extensions?.code as string);
      }) ?? false
    );
  };

  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [target] = entries;
      if (!target) return;
      if (target.isIntersecting && hasMore && !loading) {
        fetchMore({ variables: { after: pageInfo?.endCursor } });
      }
    },
    [hasMore, loading, fetchMore, pageInfo?.endCursor]
  );

  useEffect(() => {
    const element = observerTarget.current;
    if (!element) return;
    const observer = new IntersectionObserver(handleObserver, { threshold: 0.1 });
    observer.observe(element);
    return () => observer.disconnect();
  }, [handleObserver]);

  const handleContextMenu = (e: React.MouseEvent, folder: MediaHouseFolder) => {
    e.preventDefault();
    setContextMenuFolder(folder);
    setContextMenuPosition({ x: e.clientX, y: e.clientY });
  };

  const handleCreateSubfolder = () => {
    if (!contextMenuFolder) return;
    setSelectedFolder(contextMenuFolder);
    setShowCreateModal(true);
    setContextMenuFolder(null);
  };

  const handleRenameFolder = () => {
    if (!contextMenuFolder) return;
    setSelectedFolder(contextMenuFolder);
    setShowRenameModal(true);
    setContextMenuFolder(null);
  };

  return (
    <>
      {/* ── Breadcrumb Path Navigator ─────────────────────────── */}
      <nav
        className={`mb-3 flex items-center flex-wrap gap-0.5 min-h-[28px] ${
          disabled ? "opacity-40 blur-[2px] pointer-events-none" : ""
        }`}
        aria-label="Folder path"
      >
        {/* Root */}
        <button
          type="button"
          onClick={() => onFolderClick(null as unknown as string)}
          className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium transition-colors duration-150 ${
            breadcrumbs.length === 0
              ? "bg-primary-50 dark:bg-primary-900/25 text-primary-600 dark:text-primary-400"
              : "text-slate-500 dark:text-gray-400 hover:text-slate-700 dark:hover:text-gray-200 hover:bg-slate-100 dark:hover:bg-gray-800"
          }`}
        >
          <FiHome className="h-3 w-3 flex-shrink-0" />
          <span>Root</span>
        </button>

        {breadcrumbs.map((folder: MediaHouseFolder, i) => (
          <div key={folder.id} className="flex items-center gap-0.5">
            <FiChevronRight className="h-3 w-3 text-slate-300 dark:text-gray-600 flex-shrink-0" />
            <button
              type="button"
              onClick={() => onFolderClick(folder.id)}
              className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium transition-colors duration-150 ${
                i === breadcrumbs.length - 1
                  ? "bg-primary-50 dark:bg-primary-900/25 text-primary-600 dark:text-primary-400"
                  : "text-slate-500 dark:text-gray-400 hover:text-slate-700 dark:hover:text-gray-200 hover:bg-slate-100 dark:hover:bg-gray-800"
              }`}
            >
              <span className="max-w-[72px] truncate">{folder.name}</span>
            </button>
          </div>
        ))}
      </nav>

      {/* ── Folders Section ────────────────────────────────────── */}
      <div className="mb-3">
        {/* Header row — always visible so users can create their first folder */}
        <div className="flex items-center gap-1 mb-2">
          <button
            type="button"
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex items-center gap-1.5 flex-1 min-w-0 py-1 px-1.5 -mx-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-gray-800/60 transition-colors group"
          >
            <span
              className={`transition-transform duration-200 ${isExpanded ? "rotate-0" : "-rotate-90"}`}
            >
              <FiChevronDown className="h-3.5 w-3.5 text-slate-400 dark:text-gray-500 group-hover:text-slate-600 dark:group-hover:text-gray-300" />
            </span>
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400 dark:text-gray-500 group-hover:text-slate-600 dark:group-hover:text-gray-300">
              Folders
            </span>
            {totalCount > 0 && (
              <span className="ml-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-slate-100 dark:bg-gray-700 text-slate-500 dark:text-gray-400 tabular-nums">
                {totalCount}
              </span>
            )}
          </button>

          {/* New folder button */}
          <button
            type="button"
            onClick={() => {
              setSelectedFolder(null);
              setShowCreateModal(true);
            }}
            title="New folder"
            className="flex-shrink-0 p-1.5 rounded-lg text-slate-400 dark:text-gray-500 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/25 transition-colors"
          >
            <FiFolderPlus className="h-4 w-4" />
          </button>
        </div>

        {/* Folder Grid */}
        {isExpanded && (totalCount > 0 || loading || error) && (
          <div className="mb-2">
            {error ? (
              <div className="flex flex-col items-center py-6 gap-2" role="alert">
                <div className="w-10 h-10 rounded-xl bg-error-50 dark:bg-error-900/25 flex items-center justify-center">
                  <FiAlertTriangle className="text-sm text-error-500" />
                </div>
                <p className="text-[11px] font-medium text-slate-600 dark:text-gray-300">
                  {isServerError(error) ? "Cannot load folders" : "Failed to load"}
                </p>
                <Button
                  onClick={() => refetch()}
                  variant="primary"
                  size="sm"
                  leftIcon={<FiRefreshCw className="h-3 w-3" />}
                >
                  Retry
                </Button>
              </div>
            ) : loading && folders.length === 0 ? (
              <div className="grid grid-cols-2 gap-1.5">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div
                    // biome-ignore lint/suspicious/noArrayIndexKey: skeleton tiles
                    key={i}
                    className="h-9 rounded-xl bg-slate-100 dark:bg-gray-800 animate-pulse"
                    style={{ animationDelay: `${i * 80}ms` }}
                  />
                ))}
              </div>
            ) : (
              <div className="max-h-[180px] overflow-y-auto pr-0.5 gradient-scrollbar">
                <div className="grid grid-cols-2 gap-1.5">
                  {folders.map((folder, idx) => (
                    <div
                      key={folder.id}
                      className="relative group"
                      onContextMenu={(e) => handleContextMenu(e, folder)}
                    >
                      <button
                        type="button"
                        onClick={() => onFolderClick(folder.id)}
                        className="w-full flex items-center gap-2 pl-2.5 pr-7 py-2 rounded-xl text-left transition-all duration-150 overflow-hidden
                            bg-slate-50 dark:bg-gray-800/60
                            border border-slate-200 dark:border-gray-700/60
                            hover:border-primary-300 dark:hover:border-primary-700
                            hover:bg-primary-50/50 dark:hover:bg-primary-900/15
                            hover:shadow-sm"
                      >
                        <FiFolder
                          className={`flex-shrink-0 h-3.5 w-3.5 ${folderAccents[idx % folderAccents.length]}`}
                        />
                        <span className="text-[11px] font-medium text-slate-600 dark:text-gray-300 truncate group-hover:text-slate-900 dark:group-hover:text-gray-100">
                          {folder.name}
                        </span>
                      </button>

                      {/* Context menu trigger */}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleContextMenu(e as React.MouseEvent, folder);
                        }}
                        className="absolute top-1/2 -translate-y-1/2 right-1 opacity-0 group-hover:opacity-100 transition-opacity p-0.5 rounded-md text-slate-400 hover:text-slate-600 dark:text-gray-500 dark:hover:text-gray-300 hover:bg-slate-200 dark:hover:bg-gray-700"
                      >
                        <FiMoreVertical className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>

                {loading && folders.length > 0 && (
                  <div className="flex justify-center py-3">
                    <div className="w-4 h-4 rounded-full border-2 border-primary-400 border-t-transparent animate-spin" />
                  </div>
                )}

                {hasMore && <div ref={observerTarget} className="h-2" />}
              </div>
            )}
          </div>
        )}

        {/* Separator */}
        <div className="h-px bg-gradient-to-r from-transparent via-slate-200 dark:via-gray-700 to-transparent" />
      </div>

      {/* ── Context Menu ─────────────────────────────────────── */}
      {contextMenuFolder && (
        <div className="fixed inset-0 z-50" onClick={() => setContextMenuFolder(null)}>
          <div
            className="absolute bg-white/96 dark:bg-gray-800/96 backdrop-blur-md border border-slate-200/80 dark:border-gray-700/80 rounded-xl shadow-xl py-1 min-w-[168px] overflow-hidden"
            style={{ top: `${contextMenuPosition.y}px`, left: `${contextMenuPosition.x}px` }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Top accent */}
            <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-primary-500 to-secondary-500" />

            <button
              type="button"
              onClick={handleCreateSubfolder}
              className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-slate-700 dark:text-gray-200 hover:bg-primary-50 dark:hover:bg-primary-900/30 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
            >
              <span className="p-1 rounded-md bg-primary-100 dark:bg-primary-900/50 text-primary-600 dark:text-primary-400">
                <FiFolderPlus className="h-3 w-3" />
              </span>
              <span className="font-medium">New Subfolder</span>
            </button>

            {!contextMenuFolder.isSystem && (
              <>
                <div className="my-1 h-px bg-slate-100 dark:bg-gray-700" />
                <button
                  type="button"
                  onClick={handleRenameFolder}
                  className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-slate-700 dark:text-gray-200 hover:bg-slate-50 dark:hover:bg-gray-700/60 transition-colors"
                >
                  <span className="p-1 rounded-md bg-slate-100 dark:bg-gray-700 text-slate-600 dark:text-gray-300">
                    <FiFolder className="h-3 w-3" />
                  </span>
                  <span className="font-medium">Rename</span>
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Modals ───────────────────────────────────────────── */}
      <CreateFolderModal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setSelectedFolder(null);
        }}
        parentId={selectedFolder?.id || parentId}
        mediaType={mediaType}
      />

      {selectedFolder && (
        <RenameFolderModal
          isOpen={showRenameModal}
          onClose={() => {
            setShowRenameModal(false);
            setSelectedFolder(null);
          }}
          folderId={selectedFolder.id}
          currentName={selectedFolder.name}
          mediaType={mediaType}
        />
      )}
    </>
  );
}
